import * as React from 'react';

import {
  enumInboundTollingAgreementSortOption,
  InboundTollingAgreementSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
  Button,
  Select,
  Switch,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus } from 'lucide-react';

import * as state from '../../_state/inboundTollingList.state';
import { $remoteAllProcessingTypes } from '../../_state/processingTypes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InboundTollingAgreementRecord } from '../../types/inboundTollingAgreement';

import { useInboundTollingColumns } from './hooks/useInboundTollingColumns';
import { InboundTollingDialog } from './InboundTollingDialog';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: InboundTollingAgreementSortOption;
    desc: InboundTollingAgreementSortOption;
  }
> = {
  agreementNumber: {
    asc: enumInboundTollingAgreementSortOption.AgreementNumberAsc,
    desc: enumInboundTollingAgreementSortOption.AgreementNumberDesc,
  },
  effectiveDate: {
    asc: enumInboundTollingAgreementSortOption.EffectiveDateAsc,
    desc: enumInboundTollingAgreementSortOption.EffectiveDateDesc,
  },
  expirationDate: {
    asc: enumInboundTollingAgreementSortOption.ExpirationDateAsc,
    desc: enumInboundTollingAgreementSortOption.ExpirationDateDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ INBOUND TOLLING LIST VIEW ⚛️ ⚛️ ⚛️
//
export const InboundTollingListView = () => {
  const t = useTranslations();

  // States
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingAgreement, setEditingAgreement] =
    React.useState<InboundTollingAgreementRecord | null>(null);

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteInboundTollingAgreements,
  );
  const { data: processingTypesData } = useAtomValue($remoteAllProcessingTypes);

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedProcessingType, setSelectedProcessingType] = useAtom(
    state.$selectedProcessingType,
  );
  const [showInactive, setShowInactive] = useAtom(state.$showInactive);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const inboundTollingAgreements = React.useMemo(
    () => data?.inboundTollingAgreements ?? [],
    [data?.inboundTollingAgreements],
  );
  const total = data?.totalCount ?? 0;

  const emptyPanelProps = {
    title: t.tollProcessingAgreementsView.emptyState.title,
    description: (
      <span>{t.tollProcessingAgreementsView.emptyState.subtitle}</span>
    ),
    centered: true,
  };

  // Memoized values
  const processingTypes = React.useMemo(
    () => processingTypesData?.processingTypes ?? [],
    [processingTypesData?.processingTypes],
  );

  const processingTypeOptions = React.useMemo(
    () => [
      {
        key: 'all',
        label: t.tollProcessingAgreementsView.filters.allProcessingTypes,
      },
      ...processingTypes.map((pt) => ({ key: pt.id, label: pt.name })),
    ],
    [processingTypes, t],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([
          enumInboundTollingAgreementSortOption.CreatedAtDesc,
        ]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleRowClick = React.useCallback(
    ({ row }: { row: InboundTollingAgreementRecord }) => {
      setEditingAgreement(row);
      setIsDialogOpen(true);
    },
    [],
  );

  const handleCreateClick = React.useCallback(() => {
    setEditingAgreement(null);
    setIsDialogOpen(true);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setIsDialogOpen(false);
    setEditingAgreement(null);
  }, []);

  const handleDialogSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  // Other data hooks
  const columns = useInboundTollingColumns({
    onRowClick: handleRowClick,
  });

  return (
    <>
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.tollProcessingAgreementsView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedProcessingType || 'all'}
              onChange={(key) => {
                setSelectedProcessingType(
                  key === 'all' ? undefined : String(key),
                );
                setOffset(0);
              }}
              options={processingTypeOptions}
              className="w-48"
            />
            <div className="flex items-center gap-2">
              <Switch
                isSelected={showInactive ?? false}
                onChange={setShowInactive}
              />
              <span className="text-sm text-neutral/60">
                {t.tollProcessingAgreementsView.filters.showInactive}
              </span>
            </div>
          </>
        }
        actions={
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={handleCreateClick}
          >
            {t.tollProcessingAgreementsView.createTollProcessingAgreement}
          </Button>
        }
        footer={
          total > limit && (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          )
        }
      >
        <DataGrid<InboundTollingAgreementRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={inboundTollingAgreements}
          rowKey="id"
          aria-label={t.tollProcessingAgreementsView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.tollProcessingAgreementsView.content.error(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <InboundTollingDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingAgreement={editingAgreement}
      />
    </>
  );
};
